import { Component, EventEmitter, HostBinding, Input, OnChanges, Output, ChangeDetectionStrategy, SimpleChanges } from '@angular/core';
import {
  DashboardBenchmarkCell,
  DashboardChartData,
  DashboardMetricValue,
  DashboardOfficeBenchmarkMeasure,
  DashboardOfficeBenchmarkRow,
  DashboardOfficeBenchmarkView,
  DashboardSelectionContext,
  DashboardSummaryPageRequest,
  DashboardTableRow,
  DashboardWidgetResult,
  DashboardWidgetSelection
} from 'src/app/core-module/models/Dashboards/dashboard.models';
import { CHART_SERIES_COLOURS } from 'src/app/core-module/constants/chart-palette';

type DashboardChartType = 'bar' | 'line' | 'doughnut';
type DashboardChartDataRow = {
  datasetIndex: number;
  dataIndex: number;
  series: string;
  period: string;
  value: number | null;
  selection: DashboardSelectionContext | null;
};
type FrequencyTrendPoint = {
  dataIndex: number;
  value: number;
  selection: DashboardSelectionContext | null;
};
type FrequencyTrendSeries = {
  label: string;
  color: string;
  points: FrequencyTrendPoint[];
  segments: FrequencyTrendPoint[][];
};

@Component({
  selector: 'app-dashboard-widget',
  templateUrl: './dashboard-widget.component.html',
  styleUrl: './dashboard-widget.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class DashboardWidgetComponent implements OnChanges {
  @HostBinding('class.dashboard-widget-chart-data-expanded') chartDataExpanded = false;
  @Input() widget: DashboardWidgetResult;
  @Input() selected = false;
  @Input() editMode = false;
  @Input() presentationMode = false;
  @Input() compactMode = false;
  @Input() summaryPagingLoading = false;

  @Output() widgetSelected = new EventEmitter<DashboardWidgetSelection>();
  @Output() removeWidget = new EventEmitter<DashboardWidgetResult>();
  @Output() renameWidget = new EventEmitter<DashboardWidgetResult>();
  @Output() configureWidget = new EventEmitter<DashboardWidgetResult>();
  @Output() summaryPageChanged = new EventEmitter<DashboardSummaryPageRequest>();

  public readonly defaultSummaryPageSize = 5;
  public summaryPageNumber = 1;
  public benchmarkMeasureKey: DashboardOfficeBenchmarkMeasure = 'quality';
  public benchmarkPillarKey = '';

  public readonly emptyChartData: DashboardChartData = {
    labels: [],
    datasets: []
  };

  private cachedChartDataSource: any;
  private cachedChartData: DashboardChartData = this.emptyChartData;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['widget']) {
      this.summaryPageNumber = this.widget?.SummaryPageNumber ?? 1;
      if (this.isExecutiveOfficeBenchmark()) {
        this.benchmarkMeasureKey = this.widget.OfficeBenchmark?.DefaultMeasureKey || 'quality';
        this.benchmarkPillarKey = this.benchmarkPillarViews()[0]?.PillarKey || '';
      }
    }
  }

  summaryRows(): DashboardTableRow[] {
    const rows = this.widget.TableRows || [];
    if (this.isServerPagedSummary()) {
      return rows;
    }
    const start = (this.summaryPageNumber - 1) * this.summaryPageSize();
    return rows.slice(start, start + this.summaryPageSize());
  }

  summaryTotalRows(): number {
    return this.widget.SummaryTotalRows ?? (this.widget.TableRows || []).length;
  }

  summaryPageSize(): number {
    return this.widget.SummaryPageSize ?? this.defaultSummaryPageSize;
  }

  summaryTotalPages(): number {
    return Math.max(1, Math.ceil(this.summaryTotalRows() / this.summaryPageSize()));
  }

  summaryFirstRow(): number {
    return this.summaryTotalRows() === 0 ? 0 : ((this.summaryPageNumber - 1) * this.summaryPageSize()) + 1;
  }

  summaryLastRow(): number {
    return Math.min(this.summaryPageNumber * this.summaryPageSize(), this.summaryTotalRows());
  }

  hasSummaryPaging(): boolean {
    return this.widget.VisualType === 'table' && this.summaryTotalRows() > this.summaryPageSize();
  }

  changeSummaryPage(pageNumber: number, event: Event): void {
    event.stopPropagation();
    if (this.summaryPagingLoading || pageNumber < 1 || pageNumber > this.summaryTotalPages()) {
      return;
    }

    if (this.isServerPagedSummary()) {
      this.summaryPageChanged.emit({
        DashboardWidgetID: this.widget.DashboardWidgetID,
        PageNumber: pageNumber,
        PageSize: this.summaryPageSize()
      });
      return;
    }

    this.summaryPageNumber = pageNumber;
  }

  private isServerPagedSummary(): boolean {
    return typeof this.widget.SummaryTotalRows === 'number'
      && Number.isFinite(this.widget.SummaryTotalRows)
      && typeof this.widget.SummaryPageNumber === 'number'
      && Number.isFinite(this.widget.SummaryPageNumber)
      && typeof this.widget.SummaryPageSize === 'number'
      && Number.isFinite(this.widget.SummaryPageSize)
      && this.widget.SummaryPageSize > 0;
  }

  public readonly chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#0b2930',
          usePointStyle: true
        }
      }
    },
    scales: {
      x: {
        ticks: {
          color: '#0b2930'
        },
        grid: {
          color: '#e4e2da'
        }
      },
      y: {
        ticks: {
          color: '#0b2930'
        },
        grid: {
          color: '#e4e2da'
        }
      }
    }
  };

  public readonly stackedChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: {
          color: '#0b2930',
          usePointStyle: true
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        ticks: {
          color: '#0b2930'
        },
        grid: {
          color: '#e4e2da'
        }
      },
      y: {
        stacked: true,
        ticks: {
          color: '#0b2930'
        },
        grid: {
          color: '#e4e2da'
        }
      }
    }
  };

  public readonly doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#0b2930',
          usePointStyle: true
        }
      }
    }
  };

  selectWidget(): void {
    if (this.editMode || !this.supportsWholeWidgetDrilldown() || this.drilldownDisabled()) {
      return;
    }

    if (!this.widget.Selection) {
      return;
    }

    this.widgetSelected.emit(this.widgetSelection(this.widget.Selection));
  }

  selectChartSegment(event: any): void {
    if (this.editMode) {
      return;
    }

    const dataIndex = this.chartSegmentIndex(event);
    const datasetIndex = this.chartDatasetIndex(event) ?? 0;
    const dataset = this.chartData().datasets[datasetIndex];
    const selectionContext = typeof dataIndex === 'number' ? dataset?.selections?.[dataIndex] : null;
    if (!selectionContext) {
      return;
    }

    this.widgetSelected.emit(this.widgetSelection(selectionContext, this.chartSegmentLabel(event)));
  }

  chartDataRows(): DashboardChartDataRow[] {
    const chartData = this.chartData();
    return chartData.datasets.flatMap((dataset, datasetIndex) =>
      dataset.data.map((value, dataIndex) => ({
        datasetIndex,
        dataIndex,
        series: dataset.label || 'Value',
        period: dataset.pointLabels?.[dataIndex] || chartData.labels[dataIndex] || `Point ${dataIndex + 1}`,
        value,
        selection: dataset.selections?.[dataIndex] || null
      }))
    );
  }

  isBluebirdFrequencyTrend(): boolean {
    return this.widget.MetricDefinitionKey === 'bbc-frequency-score-trend';
  }

  isBluebirdRadialProgress(): boolean {
    return this.widget.MetricDefinitionKey === 'bbc-mandatory-audit-progress'
      || this.widget.MetricDefinitionKey === 'bbc-exec-compliance';
  }

  isExecutiveWidget(): boolean {
    return this.widget.MetricDefinitionKey.startsWith('bbc-exec-');
  }

  supportsWholeWidgetDrilldown(): boolean {
    if (!this.widget.Selection || this.widget.VisualType === 'table') {
      return false;
    }

    return !this.isChartWidget() || this.widget.MetricDefinitionKey === 'bbc-exec-compliance';
  }

  frequencyTrendLabels(): string[] {
    return this.chartData().labels;
  }

  frequencyTrendTicks(): number[] {
    const minimum = this.scoreTrendAxisMin();
    const step = 100 - minimum >= 60 ? 20 : 10;
    const ticks: number[] = [];
    for (let value = minimum; value < 100; value += step) {
      ticks.push(value);
    }
    ticks.push(100);
    return ticks;
  }

  frequencyTrendX(index: number): number {
    const count = this.frequencyTrendLabels().length;
    return count <= 1 ? 337 : 52 + ((570 * index) / (count - 1));
  }

  frequencyTrendPointX(point: FrequencyTrendPoint, seriesIndex: number): number {
    const matchingSeries = this.frequencyTrendSeries()
      .map((series, index) => ({ index, point: series.points.find(candidate =>
        candidate.dataIndex === point.dataIndex && candidate.value === point.value) }))
      .filter(match => !!match.point);
    const overlapIndex = matchingSeries.findIndex(match => match.index === seriesIndex);
    if (matchingSeries.length <= 1 || overlapIndex < 0) {
      return this.frequencyTrendX(point.dataIndex);
    }
    const offset = (overlapIndex - ((matchingSeries.length - 1) / 2)) * 10;
    return this.frequencyTrendX(point.dataIndex) + offset;
  }

  frequencyTrendY(value: number): number {
    const minimum = this.scoreTrendAxisMin();
    const bounded = Math.min(100, Math.max(minimum, value));
    return 216 - (((bounded - minimum) / Math.max(1, 100 - minimum)) * 196);
  }

  frequencyTrendPoints(points: FrequencyTrendPoint[]): string {
    return points.map(point => `${this.frequencyTrendX(point.dataIndex)},${this.frequencyTrendY(point.value)}`).join(' ');
  }

  frequencyTrendSeries(): FrequencyTrendSeries[] {
    const fallbackColors = CHART_SERIES_COLOURS.dashboardFallback;
    return this.chartData().datasets.map((dataset, seriesIndex) => {
      const points: FrequencyTrendPoint[] = [];
      const segments: FrequencyTrendPoint[][] = [];
      let segment: FrequencyTrendPoint[] = [];

      dataset.data.forEach((rawValue, dataIndex) => {
        const value = typeof rawValue === 'number' && Number.isFinite(rawValue) ? rawValue : null;
        if (value === null) {
          if (segment.length) {
            segments.push(segment);
            segment = [];
          }
          return;
        }
        const point = { dataIndex, value, selection: dataset.selections?.[dataIndex] || null };
        points.push(point);
        segment.push(point);
      });
      if (segment.length) {
        segments.push(segment);
      }

      const configuredColor = Array.isArray(dataset.borderColor) ? dataset.borderColor[0] : dataset.borderColor;
      return {
        label: dataset.label || `Series ${seriesIndex + 1}`,
        color: configuredColor || fallbackColors[seriesIndex % fallbackColors.length],
        points,
        segments
      };
    });
  }

  selectFrequencyTrendPoint(point: FrequencyTrendPoint, label: string, event: Event): void {
    event.stopPropagation();
    if (this.editMode || !point.selection) {
      return;
    }
    this.widgetSelected.emit(this.widgetSelection(point.selection, label));
  }

  selectFrequencyTrendPointFromKeyboard(point: FrequencyTrendPoint, label: string, event: Event): void {
    event.preventDefault();
    this.selectFrequencyTrendPoint(point, label, event);
  }

  selectChartDataPoint(row: DashboardChartDataRow, event: Event): void {
    event.stopPropagation();
    if (this.editMode || !row.selection) {
      return;
    }

    this.widgetSelected.emit(this.widgetSelection(row.selection, row.period));
  }

  selectChartDataPointFromKeyboard(row: DashboardChartDataRow, event: Event): void {
    event.preventDefault();
    this.selectChartDataPoint(row, event);
  }

  chartDataToggle(event: Event): void {
    this.chartDataExpanded = event.target instanceof HTMLDetailsElement && event.target.open;
  }

  selectTableRow(row: DashboardTableRow, event: Event): void {
    event.stopPropagation();
    if (this.editMode || !row.Selection) {
      return;
    }

    this.widgetSelected.emit(this.widgetSelection(row.Selection));
  }

  selectMetricValue(metric: DashboardMetricValue, event: Event): void {
    event.stopPropagation();
    if (this.editMode || metric.AggregateOnly || !metric.Selection) {
      return;
    }

    this.widgetSelected.emit(this.widgetSelection(metric.Selection));
  }

  selectBenchmarkCell(cell: DashboardBenchmarkCell, event: Event): void {
    event.stopPropagation();
    if (this.editMode || cell.AggregateOnly || cell.IsSuppressed || !cell.Selection) {
      return;
    }

    this.widgetSelected.emit(this.widgetSelection(cell.Selection, cell.ScopeLabel));
  }

  isBenchmarkTable(): boolean {
    return this.widget.MetricDefinitionKey === 'bbc-local-benchmark'
      && !!this.widget.BenchmarkTable;
  }

  benchmarkScopeHeaders(): DashboardBenchmarkCell[] {
    return this.widget.BenchmarkTable?.Rows?.[0]?.Cells || [];
  }

  isExecutiveOfficeBenchmark(): boolean {
    return this.widget?.MetricDefinitionKey === 'bbc-exec-office-benchmark'
      && !!this.widget.OfficeBenchmark;
  }

  benchmarkBaseViews(): DashboardOfficeBenchmarkView[] {
    return (this.widget.OfficeBenchmark?.Views || []).filter(view => view.MeasureKey !== 'pillar');
  }

  benchmarkPillarViews(): DashboardOfficeBenchmarkView[] {
    return (this.widget.OfficeBenchmark?.Views || []).filter(view => view.MeasureKey === 'pillar');
  }

  selectedBenchmarkView(): DashboardOfficeBenchmarkView | undefined {
    const views = this.widget.OfficeBenchmark?.Views || [];
    if (this.benchmarkMeasureKey === 'pillar') {
      return views.find(view => view.MeasureKey === 'pillar' && view.PillarKey === this.benchmarkPillarKey)
        || this.benchmarkPillarViews()[0];
    }
    return views.find(view => view.MeasureKey === this.benchmarkMeasureKey);
  }

  changeBenchmarkMeasure(event: Event): void {
    event.stopPropagation();
    const select = event.target as HTMLSelectElement;
    this.benchmarkMeasureKey = select.value as DashboardOfficeBenchmarkMeasure;
    if (this.benchmarkMeasureKey === 'pillar' && !this.benchmarkPillarKey) {
      this.benchmarkPillarKey = this.benchmarkPillarViews()[0]?.PillarKey || '';
    }
  }

  changeBenchmarkPillar(event: Event): void {
    event.stopPropagation();
    this.benchmarkPillarKey = (event.target as HTMLSelectElement).value;
  }

  selectOfficeBenchmarkRow(row: DashboardOfficeBenchmarkRow, event: Event): void {
    event.stopPropagation();
    if (event instanceof KeyboardEvent) {
      event.preventDefault();
    }
    if (this.editMode || !row.Selection) {
      return;
    }
    this.widgetSelected.emit(this.widgetSelection(row.Selection, row.OfficeName));
  }

  openOfficeBenchmarkRanking(event: Event): void {
    event.stopPropagation();
    const selection = this.selectedBenchmarkView()?.FullListSelection;
    if (this.editMode || !selection) {
      return;
    }
    this.widgetSelected.emit(this.widgetSelection(selection, this.selectedBenchmarkView()?.Label));
  }

  officeBenchmarkValue(row: DashboardOfficeBenchmarkRow): string {
    return row.State === 'neutral' || typeof row.CurrentValue !== 'number'
      ? '-'
      : `${Math.round(row.CurrentValue)}%`;
  }

  officeBenchmarkMovement(row: DashboardOfficeBenchmarkRow): string {
    if (typeof row.Movement !== 'number') {
      return '-';
    }
    const roundedMovement = Math.round(row.Movement);
    return `${roundedMovement > 0 ? '+' : ''}${roundedMovement} pp`;
  }

  widgetValueDisplay(): string {
    return this.percentageText(`${this.widget.ValuePrefix || ''}${this.widget.Value ?? ''}${this.widget.ValueSuffix || ''}`);
  }

  metricValueDisplay(metric: DashboardMetricValue): string {
    return this.percentageText(`${metric.Value ?? ''}${metric.Suffix || ''}`);
  }

  percentageText(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }

    return String(value).replace(/([+-]?)(\d+(?:\.\d+)?)\s*(%|pp)(?!\w)/gi, (_match, sign: string, digits: string, unit: string) => {
      const roundedValue = Math.round(Number(`${sign === '-' ? '-' : ''}${digits}`));
      const normalisedValue = Object.is(roundedValue, -0) ? 0 : roundedValue;
      const displaySign = sign === '+' && normalisedValue >= 0 ? '+' : '';
      return `${displaySign}${normalisedValue}${unit.toLowerCase() === 'pp' ? ' pp' : '%'}`;
    });
  }

  roundedTableValue(value: unknown, fallback = '-'): string {
    if (value === null || value === undefined || value === '') {
      return fallback;
    }

    const text = String(value);
    if (/^[+-]?\d+(?:\.\d+)?$/.test(text.trim())) {
      const roundedValue = Math.round(Number(text));
      const displaySign = text.trim().startsWith('+') && roundedValue >= 0 ? '+' : '';
      return `${displaySign}${Object.is(roundedValue, -0) ? 0 : roundedValue}`;
    }

    return this.percentageText(text);
  }

  chartDataValue(row: DashboardChartDataRow): string | number {
    if (row.value === null) {
      return 'No data';
    }

    return this.chartUsesPercentageValues() ? `${Math.round(row.value)}%` : row.value;
  }

  selectWidgetFromKeyboard(event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.selectWidget();
  }

  remove(event: Event): void {
    event.stopPropagation();
    this.removeWidget.emit(this.widget);
  }

  rename(event: Event): void {
    event.stopPropagation();
    this.renameWidget.emit(this.widget);
  }

  configure(event: Event): void {
    event.stopPropagation();
    this.configureWidget.emit(this.widget);
  }

  chartType(): DashboardChartType {
    if (this.widget.VisualType === 'line' || this.widget.VisualType === 'doughnut') {
      return this.widget.VisualType;
    }

    if (this.widget.VisualType === 'radialProgress') {
      return 'doughnut';
    }

    return 'bar';
  }

  chartOptionsFor(): object {
    if (this.widget.VisualType === 'doughnut') {
      return this.doughnutOptions;
    }

    if (this.widget.VisualType === 'radialProgress') {
      return {
        ...this.doughnutOptions,
        cutout: '76%',
        plugins: {
          ...this.doughnutOptions.plugins,
          legend: { display: false }
        }
      };
    }

    if (this.widget.VisualType === 'stackedBar') {
      return this.stackedChartOptions;
    }

    if (this.widget.MetricDefinitionKey === 'score-trend') {
      return this.scoreTrendChartOptions();
    }

    if (this.widget.MetricDefinitionKey === 'bbc-frequency-score-trend') {
      return this.frequencyScoreTrendChartOptions();
    }

    if (this.widget.MetricDefinitionKey === 'bbc-exec-monthly-quality-trend'
      || this.widget.MetricDefinitionKey === 'bbc-exec-yoy-comparison') {
      return this.executiveQualityTrendChartOptions();
    }

    return this.chartOptions;
  }

  chartData(): DashboardChartData {
    const chartData = this.widget.ChartData as any;
    if (!chartData) {
      this.cachedChartDataSource = null;
      this.cachedChartData = this.emptyChartData;
      return this.emptyChartData;
    }

    if (chartData === this.cachedChartDataSource) {
      return this.cachedChartData;
    }

    const datasets = chartData.datasets || chartData.Datasets || [];

    this.cachedChartDataSource = chartData;
    this.cachedChartData = {
      labels: chartData.labels || chartData.Labels || [],
      datasets: datasets.map((dataset: any) => ({
        label: dataset.label ?? dataset.Label,
        data: dataset.values ?? dataset.Values ?? dataset.data ?? dataset.Data ?? [],
        values: dataset.values ?? dataset.Values,
        pointLabels: dataset.pointLabels ?? dataset.PointLabels ?? [],
        backgroundColor: dataset.backgroundColor ?? dataset.BackgroundColor,
        borderColor: dataset.borderColor ?? dataset.BorderColor,
        borderWidth: dataset.borderWidth ?? dataset.BorderWidth,
        borderDash: dataset.borderDash ?? dataset.BorderDash,
        pointStyle: dataset.pointStyle ?? dataset.PointStyle,
        tension: dataset.tension ?? dataset.Tension,
        fill: dataset.fill ?? dataset.Fill,
        selections: dataset.selections ?? dataset.Selections ?? []
      }))
    };

    return this.cachedChartData;
  }

  chartSegmentLabel(event: any): string {
    const chartData = this.chartData();
    const index = this.chartSegmentIndex(event);
    if (typeof index !== 'number' || index < 0 || index >= chartData.labels.length) {
      return '';
    }

    return chartData.labels[index] || '';
  }

  chartDatasetLabel(event: any): string {
    const chartData = this.chartData();
    const index = this.chartDatasetIndex(event);
    if (typeof index !== 'number' || index < 0 || index >= chartData.datasets.length) {
      return '';
    }

    return chartData.datasets[index].label || '';
  }

  private chartSegmentIndex(event: any): number | undefined {
    const activeElement = Array.isArray(event?.element) ? event.element[0] : event?.element;
    const chartElement = activeElement?.element ?? activeElement;

    return activeElement?.dataIndex
      ?? activeElement?.$context?.dataIndex
      ?? chartElement?.$context?.dataIndex
      ?? chartElement?.dataIndex
      ?? event?.dataIndex
      ?? event?.index
      ?? activeElement?._index
      ?? chartElement?._index
      ?? activeElement?.index;
  }

  private chartDatasetIndex(event: any): number | undefined {
    const activeElement = Array.isArray(event?.element) ? event.element[0] : event?.element;
    const chartElement = activeElement?.element ?? activeElement;
    const eventDataset = Array.isArray(event?.dataset) ? event.dataset[0] : event?.dataset;

    return activeElement?.datasetIndex
      ?? activeElement?.$context?.datasetIndex
      ?? chartElement?.$context?.datasetIndex
      ?? chartElement?.datasetIndex
      ?? event?.datasetIndex
      ?? eventDataset?.datasetIndex
      ?? activeElement?._datasetIndex
      ?? chartElement?._datasetIndex;
  }

  private scoreTrendChartOptions(): object {
    return {
      ...this.chartOptions,
      plugins: {
        ...this.chartOptions.plugins,
        tooltip: {
          callbacks: {
            label: (item: any) => item?.raw === null || item?.raw === undefined
              ? `${item?.dataset?.label || ''}: No data`
              : `${item?.dataset?.label || ''}: ${Math.round(Number(item.raw))}%`
          }
        }
      },
      scales: {
        ...this.chartOptions.scales,
        y: {
          ...this.chartOptions.scales.y,
          min: this.scoreTrendAxisMin(),
          max: 100,
          ticks: {
            ...this.chartOptions.scales.y.ticks,
            callback: (value: number | string) => `${Math.round(Number(value))}%`
          }
        }
      }
    };
  }

  private scoreTrendAxisMin(): number {
    const values: number[] = [];

    for (const dataset of this.chartData().datasets || []) {
      for (const value of dataset.data || []) {
        if (value === null || value === undefined) {
          continue;
        }
        const numericValue = Number(value);
        if (Number.isFinite(numericValue)) {
          values.push(numericValue);
        }
      }
    }

    if (!values.length) {
      return 0;
    }

    const roundedDownWithMargin = (Math.floor(Math.min(...values) / 10) * 10) - 10;
    return Math.max(0, Math.min(roundedDownWithMargin, 90));
  }

  isChartWidget(): boolean {
    return this.widget.VisualType === 'bar'
      || this.widget.VisualType === 'line'
      || this.widget.VisualType === 'doughnut'
      || this.widget.VisualType === 'stackedBar'
      || this.widget.VisualType === 'radialProgress';
  }

  hasRequirementColumns(): boolean {
    return (this.widget.TableRows || []).some(row => !!row.Frequency || !!row.Period || !!row.CompletionDate);
  }

  hasQualityColumns(): boolean {
    return this.widget.MetricDefinitionKey === 'bbc-pillar-performance'
      || this.widget.MetricDefinitionKey === 'bbc-lowest-pillars';
  }

  isRepeatedFindingsTable(): boolean {
    return this.widget.MetricDefinitionKey === 'bbc-repeated-findings';
  }

  isExecutivePriorityQuestionsTable(): boolean {
    return this.widget.MetricDefinitionKey === 'bbc-exec-improvement-priorities';
  }

  isExecutiveRegionalComparisonTable(): boolean {
    return this.widget.MetricDefinitionKey === 'bbc-exec-regional-comparison';
  }

  regionalComparisonRegions(): string[] {
    return [...new Set((this.widget.TableRows || []).map(row => row.Category || row.Detail).filter(Boolean))];
  }

  regionalComparisonMeasures(): string[] {
    return [...new Set((this.widget.TableRows || []).map(row => row.Label).filter(Boolean))];
  }

  regionalComparisonCell(region: string, measure: string): DashboardTableRow | undefined {
    return (this.widget.TableRows || []).find(row =>
      (row.Category || row.Detail) === region && row.Label === measure);
  }

  priorityQuestionsEmptyMessage(): string {
    return this.widget.Status === 'error'
      ? 'Priority questions could not be loaded.'
      : 'No scored priority questions are available.';
  }

  drilldownDisabled(): boolean {
    if (this.widget.Status === 'error') {
      return true;
    }

    return ((this.widget.MetricDefinitionKey === 'completion-rate' || this.widget.MetricDefinitionKey === 'missed-audits')
      && this.widget.Status === 'neutral'
      && typeof this.widget.Value === 'string'
      && this.widget.Value.toLowerCase() === 'no scheduled audits')
      || (!this.isChartWidget() && this.widget.VisualType !== 'table' && !this.widget.Selection);
  }

  widgetStatusClass(): string {
    if (this.widget.Status === 'danger') {
      return 'dashboard-widget-danger';
    }

    if (this.widget.Status === 'warning') {
      return 'dashboard-widget-warning';
    }

    if (this.widget.Status === 'neutral' || this.widget.Status === 'error') {
      return 'dashboard-widget-neutral';
    }

    return 'dashboard-widget-ok';
  }

  statusAccessibilityText(status: string): string {
    const labels: Record<string, string> = {
      ok: 'OK status',
      warning: 'Warning status',
      danger: 'Danger status',
      neutral: 'Neutral status',
      error: 'Error status'
    };

    return labels[status] || 'Status unavailable';
  }

  widgetStatusAccessibilityText(): string {
    return this.statusAccessibilityText(this.widget.Status);
  }

  valueClass(): string {
    const baseClass = 'dashboard-widget-value font-bold text-offblack';
    return typeof this.widget.Value === 'string'
      ? `${baseClass} dashboard-widget-value-text`
      : `${baseClass} text-4xl md:text-5xl`;
  }

  trendClass(): string {
    if (!this.widget.TrendDirection || this.widget.TrendDirection === 'flat') {
      return 'trend-flat';
    }

    const positiveDirection = this.widget.PositiveOutcomeDirection ?? 'higher';
    const isPositive =
      (positiveDirection === 'higher' && this.widget.TrendDirection === 'up') ||
      (positiveDirection === 'lower' && this.widget.TrendDirection === 'down');

    return isPositive ? 'trend-positive' : 'trend-negative';
  }

  private widgetSelection(selection: DashboardSelectionContext, segmentLabel = ''): DashboardWidgetSelection {
    return {
      Widget: this.widget,
      SelectionKey: selection.SelectionKey,
      SegmentLabel: segmentLabel || undefined,
      PeriodStart: selection.PeriodStart || undefined,
      PeriodEnd: selection.PeriodEnd || undefined,
      PeriodKey: selection.PeriodKey || undefined,
      FrequencyID: selection.FrequencyID,
      AuditID: selection.AuditID,
      PillarID: selection.PillarID,
      PillarKey: selection.PillarKey || undefined,
      QuestionID: selection.QuestionID,
      ScopeType: selection.ScopeType || undefined,
      ScopeID: selection.ScopeID,
      RowType: selection.RowType
    };
  }

  private frequencyScoreTrendChartOptions(): object {
    return {
      ...this.scoreTrendChartOptions(),
      plugins: {
        ...this.chartOptions.plugins,
        tooltip: {
          callbacks: {
            title: (items: any[]) => {
              const item = items?.[0];
              const pointLabels = item?.dataset?.pointLabels as string[] | undefined;
              return pointLabels?.[item?.dataIndex] || item?.label || '';
            },
            label: (item: any) => item?.raw === null || item?.raw === undefined
              ? `${item?.dataset?.label || ''}: No data`
              : `${item?.dataset?.label || ''}: ${Math.round(Number(item.raw))}%`
          }
        }
      }
    };
  }

  private executiveQualityTrendChartOptions(): object {
    return {
      ...this.chartOptions,
      elements: {
        line: { borderWidth: 3 },
        point: { radius: 4, hoverRadius: 6 }
      },
      plugins: {
        ...this.chartOptions.plugins,
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            ...this.chartOptions.plugins.legend.labels,
            boxWidth: 12,
            padding: 12
          }
        },
        tooltip: {
          callbacks: {
            label: (item: any) => item?.raw === null || item?.raw === undefined
              ? `${item?.dataset?.label || ''}: No data`
              : `${item?.dataset?.label || ''}: ${Math.round(Number(item.raw))}%`
          }
        }
      },
      scales: {
        ...this.chartOptions.scales,
        y: {
          ...this.chartOptions.scales.y,
          min: 0,
          max: 100,
          ticks: {
            ...this.chartOptions.scales.y.ticks,
            callback: (value: number | string) => `${Math.round(Number(value))}%`
          }
        }
      }
    };
  }

  private chartUsesPercentageValues(): boolean {
    const percentageMetricKeys = [
      'score-trend',
      'bbc-frequency-score-trend',
      'bbc-exec-monthly-quality-trend',
      'bbc-exec-yoy-comparison'
    ];

    return percentageMetricKeys.includes(this.widget.MetricDefinitionKey)
      || this.widget.ValueSuffix === '%'
      || this.chartData().datasets.some(dataset => /%|percentage|score|rate/i.test(dataset.label || ''));
  }
}
